import React, { Fragment, useRef, ReactElement } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { IS_ALLMYLES } from 'utils/brand';
import { useTranslation } from 'next-i18next/pages';

import { useAppSelector } from 'store';

interface Props {
  children?: ReactElement | Array<ReactElement>;
  cancelDisabled?: boolean;
  openExtras?: boolean;
  content?: any;
  modalOpen: boolean;
  closeModal: () => void;
  isCartModal?: boolean;
  isPurchaseStatusModal?: boolean;
  isExpiredSessionModal?: boolean;
}

const BaseModal: React.FC<Props> = ({
  children,
  cancelDisabled,
  modalOpen,
  closeModal,
  isCartModal,
  isPurchaseStatusModal,
  isExpiredSessionModal,
}) => {
  const { t } = useTranslation();

  const cancelButtonRef = useRef(null);

  const state = useAppSelector(({ base }) => ({
    expired1ModalOpen: base.expired1ModalOpen,
    expired2ModalOpen: base.expired2ModalOpen,
  }));

  if (
    !isExpiredSessionModal &&
    (state.expired1ModalOpen || state.expired2ModalOpen)
  )
    return null;

  return (
    <Transition.Root show={modalOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={cancelButtonRef}
        onClose={() => {
          !cancelDisabled && closeModal();
        }}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          {/* Allmyles dims the page in its navy (WHIT-493). */}
          <div
            className={`fixed inset-0 transition-opacity ${
              IS_ALLMYLES ? 'bg-[rgba(10,13,53,0.55)]' : 'bg-gray-500/75'
            }`}
          />
        </Transition.Child>

        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div
            className={`flex min-h-full justify-center text-center ${
              isCartModal ? 'items-end sm:items-center' : 'p-4  items-center'
            }`}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <Dialog.Panel
                className={`relative bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all ${
                  isPurchaseStatusModal
                    ? 'sm:max-w-3xl sm:w-full'
                    : 'sm:my-8 sm:max-w-lg w-full'
                }`}>
                <div className="sm:block absolute top-0 right-0 pt-4 pr-4">
                  {!cancelDisabled && (
                    <button
                      ref={cancelButtonRef}
                      type="button"
                      className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      onClick={closeModal}>
                      <span className="sr-only">{t('Close')}</span>
                      <XMarkIcon
                        className="h-6 w-6 text-tt-dark-purple"
                        aria-hidden="true"
                      />
                    </button>
                  )}
                </div>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  {/* Main content */}
                  {children}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default BaseModal;
